import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Send, Eye, Loader2, Monitor, Smartphone, RefreshCcw, Trash2, Clock, XCircle, Users, Download, BarChart3, Mail, MousePointerClick } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Campaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewContent, setPreviewContent] = useState({ subject: '', content: '' });
  const [previewMode, setPreviewMode] = useState('desktop');
  const [sendingCampaign, setSendingCampaign] = useState(null);
  const [recipientInput, setRecipientInput] = useState('');
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  const [recipientsDialog, setRecipientsDialog] = useState({ open: false, campaign: null });
  const [statsDialog, setStatsDialog] = useState({ open: false, campaign: null, data: null, loading: false });

  const openStats = async (campaign) => {
    setStatsDialog({ open: true, campaign, data: null, loading: true });
    try {
      const res = await axios.get(`${API}/campaigns/${campaign.id}/stats`);
      setStatsDialog({ open: true, campaign, data: res.data, loading: false });
    } catch (e) {
      toast.error('No se pudieron cargar las estadísticas');
      setStatsDialog({ open: false, campaign: null, data: null, loading: false });
    }
  };
  
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    content: '',
    segment: 'all'
  });

  const parseRecipients = (value) => value
    .split(/[\n,;]+/)
    .map((email) => email.trim())
    .filter(Boolean);

  const fetchCampaigns = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/campaigns`);
      setCampaigns(response.data);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error('Error al cargar las campañas');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
    fetchTemplates();
  }, [fetchCampaigns, fetchTemplates]);

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    try {
      const recipients = parseRecipients(recipientInput);
      const payload = { ...formData };
      if (recipients.length > 0) {
        payload.recipients = recipients;
      }
      if (scheduleEnabled && scheduledAt) {
        payload.scheduled_at = scheduledAt;
      }
      await axios.post(`${API}/campaigns`, payload);
      const msg = scheduleEnabled && scheduledAt
        ? `Campaña programada para ${formatScheduledDate(scheduledAt)}`
        : 'Campaña creada exitosamente';
      toast.success(msg);
      setShowCreateDialog(false);
      setFormData({ name: '', subject: '', content: '', segment: 'all' });
      setRecipientInput('');
      setScheduleEnabled(false);
      setScheduledAt('');
      fetchCampaigns();
    } catch (error) {
      console.error('Error creating campaign:', error);
      toast.error('Error al crear la campaña');
    }
  };

  const handleSendCampaign = async (campaign, isResend = false) => {
    const hasRecipients = campaign.recipients && campaign.recipients.length > 0;
    const confirmMessage = hasRecipients
      ? `¿Estás seguro${isResend ? ' de reenvíar' : ''} esta campaña? Se envíara a ${campaign.recipients.length} correos específicos.`
      : `¿Estás seguro${isResend ? ' de reenvíar' : ''} esta campaña? Se envíara a todos los contactos del segmento seleccionado.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    setSendingCampaign(campaign.id);
    try {
      const response = await axios.post(`${API}/campaigns/${campaign.id}/send`);
      const { sent, failed, total } = response.data;
      if (failed > 0) {
        const summary = `Enviados: ${sent}, fallidos: ${failed} de ${total}.`;
        if (sent === 0) {
          toast.error(`No se pudo envíar la campaña. ${summary} Revisa la verificación del dominio en Resend.`);
        } else {
          toast.warning(`Campaña envíada con incidencias. ${summary}`);
        }
      } else {
        toast.success(`${isResend ? 'Campaña reenvíada' : 'Campaña envíada'}: ${sent} emails envíados`);
      }
      fetchCampaigns();
    } catch (error) {
      console.error('Error sending campaign:', error);
      toast.error('Error al envíar la campaña. Verifica tu API Key de Resend.');
    } finally {
      setSendingCampaign(null);
    }
  };

  const handleCancelScheduled = async (campaignId) => {
    if (!window.confirm('¿Cancelar el envío programado? La campaña pasará a borrador.')) {
      return;
    }
    try {
      await axios.post(`${API}/campaigns/${campaignId}/cancel`);
      toast.success('Envio programado cancelado');
      fetchCampaigns();
    } catch (error) {
      console.error('Error cancelling campaign:', error);
      toast.error('Error al cancelar la campaña programada');
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta campaña? Esta acción no se puede deshacer.')) {
      return;
    }
    try {
      await axios.delete(`${API}/campaigns/${campaignId}`);
      toast.success('Campaña eliminada');
      fetchCampaigns();
    } catch (error) {
      console.error('Error deleting campaign:', error);
      toast.error('Error al eliminar la campaña');
    }
  };

  const handlePreview = (campaign) => {
    setPreviewContent({
      subject: campaign.subject,
      content: campaign.content
    });
    setPreviewMode('desktop');
    setShowPreviewDialog(true);
  };

  const handleUseTemplate = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setFormData({
        ...formData,
        subject: template.subject,
        content: template.content,
        segment: template.segment
      });
    }
  };

  const formatScheduledDate = (dateStr) => {
    if (!dateStr) return '';
    const [datePart, timePart] = dateStr.split('T');
    const [year, month, day] = datePart.split('-');
    return `${day}/${month}/${year} a las ${timePart}h`;
  };

  const getStatusBadge = (status, scheduledAt) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700',
      scheduled: 'bg-amber-100 text-amber-700',
      sending: 'bg-blue-100 text-blue-700',
      sent: 'bg-green-100 text-green-700'
    };
    const labels = {
      draft: 'Borrador',
      scheduled: 'Programado',
      sending: 'Enviando',
      sent: 'Enviado'
    };
    return (
      <div className="flex flex-col gap-1">
        <Badge className={styles[status] || styles.draft} data-testid="campaign-status-badge">
          {status === 'scheduled' && <Clock className="h-3 w-3 mr-1" />}
          {labels[status] || status}
        </Badge>
        {status === 'scheduled' && scheduledAt && (
          <span className="text-xs text-amber-600 font-medium" data-testid="campaign-scheduled-time">
            {formatScheduledDate(scheduledAt)}
          </span>
        )}
      </div>
    );
  };

  const getSegmentLabel = (segment, recipients = []) => {
    if (recipients && recipients.length > 0) {
      return `Específicos (${recipients.length})`;
    }
    const labels = {
      all: 'Todos',
      empresa: 'Empresas',
      asociacion: 'Asociaciones',
      particular: 'Particulares',
      agencia_de_viajes: 'Agencias de Viajes',
      colegio: 'Colegios',
      ninguno: 'Ninguno'
    };
    return labels[segment] || segment;
  };

  // Get minimum datetime for scheduling (now + 5 min, in Madrid approx)
  const getMinScheduleTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 5);
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Campañas de Email</h1>
          <p className="mt-2 text-gray-600">Crea y gestiona tus campañas de email marketing</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={(open) => {
          setShowCreateDialog(open);
          if (!open) {
            setScheduleEnabled(false);
            setScheduledAt('');
          }
        }}>
          <DialogTrigger asChild>
            <Button data-testid="create-campaign-button">
              <Plus className="h-4 w-4 mr-2" />
              Nueva Campaña
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-campaign-dialog">
            <DialogHeader>
              <DialogTitle>Crear Nueva Campaña</DialogTitle>
              <DialogDescription>
                Configura tu campaña de email. Usa {"{{name}}"} y {"{{company}}"} para personalizar.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateCampaign} className="space-y-4">
              <div>
                <Label htmlFor="campaign-name">Nombre de la Campaña *</Label>
                <Input
                  id="campaign-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ej: Promoción Salas de Reuniones - Empresas"
                  required
                  data-testid="campaign-name-input"
                />
              </div>
              
              {templates.length > 0 && (
                <div>
                  <Label htmlFor="use-template">Usar Plantilla (Opcional)</Label>
                  <Select onValueChange={handleUseTemplate}>
                    <SelectTrigger data-testid="campaign-use-template-select">
                      <SelectValue placeholder="Selecciona una plantilla" />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map(template => (
                        <SelectItem key={template.id} value={template.id} data-testid={`campaign-template-${template.id}`}>
                          {template.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div>
                <Label htmlFor="segment">Segmento *</Label>
                <Select value={formData.segment} onValueChange={(value) => setFormData({ ...formData, segment: value })}>
                  <SelectTrigger data-testid="campaign-segment-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" data-testid="campaign-segment-all">Todos los Contactos</SelectItem>
                    <SelectItem value="empresa" data-testid="campaign-segment-empresa">Solo Empresas</SelectItem>
                    <SelectItem value="asociacion" data-testid="campaign-segment-asociacion">Solo Asociaciones</SelectItem>
                    <SelectItem value="particular" data-testid="campaign-segment-particular">Solo Particulares</SelectItem>
                    <SelectItem value="agencia_de_viajes" data-testid="campaign-segment-agencia">Solo Agencias de Viajes</SelectItem>
                    <SelectItem value="colegio" data-testid="campaign-segment-colegio">Solo Colegios</SelectItem>
                    <SelectItem value="ninguno" data-testid="campaign-segment-ninguno">Ninguno (solo correos específicos)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="recipients">Enviar a correos específicos (opcional)</Label>
                <Textarea
                  id="recipients"
                  value={recipientInput}
                  onChange={(e) => setRecipientInput(e.target.value)}
                  rows={3}
                  placeholder="email1@dominio.com, email2@dominio.com"
                  data-testid="campaign-recipients-input"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Separa con coma o salto de línea. Si rellenas este campo, se ignora el segmento.
                </p>
              </div>
              
              <div>
                <Label htmlFor="subject">Asunto del Email *</Label>
                <Input
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Ej: Oferta especial en salas de reuniones"
                  required
                  data-testid="campaign-subject-input"
                />
              </div>
              
              <div>
                <Label htmlFor="content">Contenido del Email (HTML) *</Label>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  rows={10}
                  placeholder={"Hola {{name}},\n\nNos complace ofrecerte...\n\nSaludos,\nEquipo de {{company}}"}
                  required
                  className="font-mono text-sm"
                  data-testid="campaign-content-textarea"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Usa {"{{name}}"} para el nombre y {"{{company}}"} para la empresa del contacto
                </p>
              </div>

              {/* Schedule Section */}
              <div className="border rounded-lg p-4 bg-gray-50 space-y-3" data-testid="schedule-section">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="schedule-toggle"
                    checked={scheduleEnabled}
                    onChange={(e) => {
                      setScheduleEnabled(e.target.checked);
                      if (!e.target.checked) setScheduledAt('');
                    }}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    data-testid="schedule-toggle"
                  />
                  <Label htmlFor="schedule-toggle" className="flex items-center gap-2 cursor-pointer font-medium">
                    <Clock className="h-4 w-4 text-amber-600" />
                    Programar envío
                  </Label>
                </div>
                {scheduleEnabled && (
                  <div className="pl-7">
                    <Label htmlFor="scheduled-at" className="text-sm text-gray-600">
                      Fecha y hora de envío (hora de Madrid)
                    </Label>
                    <Input
                      id="scheduled-at"
                      type="datetime-local"
                      value={scheduledAt}
                      onChange={(e) => setScheduledAt(e.target.value)}
                      min={getMinScheduleTime()}
                      required={scheduleEnabled}
                      className="mt-1 max-w-xs"
                      data-testid="scheduled-at-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Zona horaria: Europa/Madrid (CET/CEST)
                    </p>
                  </div>
                )}
              </div>
              
              <div className="flex gap-2">
                <Button type="submit" className="flex-1" data-testid="confirm-create-campaign-button">
                  {scheduleEnabled && scheduledAt ? (
                    <><Clock className="h-4 w-4 mr-2" /> Programar Campaña</>
                  ) : (
                    'Crear Campaña'
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateDialog(false)}
                  data-testid="cancel-create-campaign-button"
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Campañas Creadas</CardTitle>
          <CardDescription>
            Gestiona y envía tus campañas de email
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Cargando campañas...</div>
          ) : campaigns.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No hay campañas creadas. Crea tu primera campaña para comenzar.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Segmento</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Enviados</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaigns.map((campaign) => (
                    <TableRow key={campaign.id} data-testid={`campaign-row-${campaign.id}`}>
                      <TableCell className="font-medium" data-testid={`campaign-name-${campaign.id}`}>{campaign.name}</TableCell>
                      <TableCell data-testid={`campaign-segment-${campaign.id}`}>{getSegmentLabel(campaign.segment, campaign.recipients)}</TableCell>
                      <TableCell data-testid={`campaign-status-${campaign.id}`}>{getStatusBadge(campaign.status, campaign.scheduled_at)}</TableCell>
                      <TableCell data-testid={`campaign-sent-${campaign.id}`}>{campaign.sent_count}</TableCell>
                      <TableCell className="text-right space-x-1" data-testid={`campaign-actions-${campaign.id}`}>
                        {campaign.status === 'sent' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openStats(campaign)}
                            data-testid={`stats-campaign-${campaign.id}`}
                            title="Ver aperturas y clicks"
                          >
                            <BarChart3 className="h-4 w-4 mr-1" />
                            Stats
                          </Button>
                        )}
                        {campaign.recipients && campaign.recipients.length > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setRecipientsDialog({ open: true, campaign })}
                            data-testid={`recipients-campaign-${campaign.id}`}
                            title="Ver lista de destinatarios"
                          >
                            <Users className="h-4 w-4 mr-1" />
                            Lista
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handlePreview(campaign)}
                          data-testid={`preview-campaign-${campaign.id}`}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Ver
                        </Button>
                        {campaign.status === 'draft' && (
                          <Button
                            size="sm"
                            onClick={() => handleSendCampaign(campaign)}
                            disabled={sendingCampaign === campaign.id}
                            data-testid={`send-campaign-${campaign.id}`}
                          >
                            {sendingCampaign === campaign.id ? (
                              <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Enviando...</>
                            ) : (
                              <><Send className="h-4 w-4 mr-1" /> Enviar</>
                            )}
                          </Button>
                        )}
                        {campaign.status === 'scheduled' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-amber-700 border-amber-300 hover:bg-amber-50"
                            onClick={() => handleCancelScheduled(campaign.id)}
                            data-testid={`cancel-scheduled-${campaign.id}`}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Cancelar
                          </Button>
                        )}
                        {campaign.status === 'sent' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSendCampaign(campaign, true)}
                            disabled={sendingCampaign === campaign.id}
                            data-testid={`resend-campaign-${campaign.id}`}
                          >
                            {sendingCampaign === campaign.id ? (
                              <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Reenvíando...</>
                            ) : (
                              <><RefreshCcw className="h-4 w-4 mr-1" /> Reenvíar</>
                            )}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteCampaign(campaign.id)}
                          data-testid={`delete-campaign-${campaign.id}`}
                        >
                          <Trash2 className="h-4 w-4 mr-1 text-red-600" />
                          Eliminar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-2xl" data-testid="campaign-preview-dialog">
          <DialogHeader>
            <DialogTitle>Vista Previa de la Campaña</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant={previewMode === 'desktop' ? 'default' : 'outline'}
                onClick={() => setPreviewMode('desktop')}
                data-testid="preview-mode-desktop"
              >
                <Monitor className="h-4 w-4 mr-2" />
                Escritorio
              </Button>
              <Button
                type="button"
                size="sm"
                variant={previewMode === 'mobile' ? 'default' : 'outline'}
                onClick={() => setPreviewMode('mobile')}
                data-testid="preview-mode-mobile"
              >
                <Smartphone className="h-4 w-4 mr-2" />
                Móvil
              </Button>
            </div>
            <div
              className={previewMode === 'mobile'
                ? 'max-w-[380px] mx-auto w-full bg-gray-50 border border-gray-200 rounded-3xl p-4 shadow-sm'
                : 'w-full'}
              data-testid="campaign-preview-frame"
            >
              <div>
                <Label>Asunto:</Label>
                <div className="mt-1 p-3 bg-white rounded border" data-testid="campaign-preview-subject">
                  {previewContent.subject}
                </div>
              </div>
              <div className="mt-4">
                <Label>Contenido:</Label>
                <div className="mt-1 p-4 bg-white rounded border max-h-96 overflow-y-auto" data-testid="campaign-preview-content">
                  <div
                    className="prose max-w-none"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewContent.content || '') }}
                  />
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recipients List Dialog */}
      <Dialog open={recipientsDialog.open} onOpenChange={(o) => setRecipientsDialog({ open: o, campaign: recipientsDialog.campaign })}>
        <DialogContent className="max-w-2xl" data-testid="recipients-dialog">
          <DialogHeader>
            <DialogTitle>Lista de destinatarios</DialogTitle>
            <DialogDescription>
              {recipientsDialog.campaign?.name} · {recipientsDialog.campaign?.recipients?.length || 0} emails
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-gray-600">
              Estado: <span className="font-semibold">{recipientsDialog.campaign?.status}</span>
              {recipientsDialog.campaign?.sent_count > 0 && (
                <> · Enviados: <span className="font-semibold">{recipientsDialog.campaign.sent_count}</span></>
              )}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const emails = recipientsDialog.campaign?.recipients || [];
                const csv = 'email\n' + emails.join('\n');
                const blob = new Blob([csv], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `destinatarios-${(recipientsDialog.campaign?.name || 'campaign').replace(/[^a-z0-9]/gi, '-').toLowerCase()}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              data-testid="download-recipients-csv"
            >
              <Download className="h-4 w-4 mr-1" />
              CSV
            </Button>
          </div>
          <div className="max-h-96 overflow-y-auto border rounded-lg" data-testid="recipients-list">
            {(recipientsDialog.campaign?.recipients || []).map((email, idx) => (
              <div key={email} className="px-3 py-2 border-b last:border-0 text-sm font-mono flex items-center justify-between hover:bg-gray-50">
                <span className="text-gray-500 w-10">{idx + 1}.</span>
                <span className="flex-1">{email}</span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      {/* Stats Dialog */}
      <Dialog open={statsDialog.open} onOpenChange={(o) => setStatsDialog({ ...statsDialog, open: o })}>
        <DialogContent className="max-w-3xl" data-testid="stats-dialog">
          <DialogHeader>
            <DialogTitle>Estadísticas de la campaña</DialogTitle>
            <DialogDescription>
              {statsDialog.campaign?.name}
            </DialogDescription>
          </DialogHeader>
          {statsDialog.loading ? (
            <div className="py-8 text-center text-gray-500">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" /> Cargando estadísticas...
            </div>
          ) : statsDialog.data ? (
            <div className="space-y-4">
              {/* KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {(() => {
                  const c = statsDialog.data.counters || {};
                  const sent = c.sent || statsDialog.campaign?.sent_count || 0;
                  const openRate = sent > 0 ? Math.round((c.opened / sent) * 100) : 0;
                  const clickRate = sent > 0 ? Math.round((c.clicked / sent) * 100) : 0;
                  return (
                    <>
                      <div className="bg-blue-50 rounded-lg p-4 text-center">
                        <Mail className="h-5 w-5 text-blue-600 mx-auto mb-1" />
                        <p className="text-2xl font-bold text-blue-900">{sent}</p>
                        <p className="text-xs text-blue-700">Enviados</p>
                      </div>
                      <div className="bg-emerald-50 rounded-lg p-4 text-center">
                        <Eye className="h-5 w-5 text-emerald-600 mx-auto mb-1" />
                        <p className="text-2xl font-bold text-emerald-900">{c.opened || 0}</p>
                        <p className="text-xs text-emerald-700">Abiertos ({openRate}%)</p>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-4 text-center">
                        <MousePointerClick className="h-5 w-5 text-purple-600 mx-auto mb-1" />
                        <p className="text-2xl font-bold text-purple-900">{c.clicked || 0}</p>
                        <p className="text-xs text-purple-700">Clicks ({clickRate}%)</p>
                      </div>
                      <div className="bg-red-50 rounded-lg p-4 text-center">
                        <XCircle className="h-5 w-5 text-red-600 mx-auto mb-1" />
                        <p className="text-2xl font-bold text-red-900">{(c.bounced || 0) + (c.complained || 0)}</p>
                        <p className="text-xs text-red-700">Rebotes / quejas</p>
                      </div>
                    </>
                  );
                })()}
              </div>
              {/* Per-recipient table */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-gray-700">Actividad por destinatario</p>
                  {statsDialog.data.recipients?.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const rows = ['email,entregado,abierto,clic,rebote'];
                        statsDialog.data.recipients.forEach(r => {
                          rows.push(`${r.email},${r.delivered?'sí':'no'},${r.opened?'sí':'no'},${r.clicked?'sí':'no'},${r.bounced?'sí':'no'}`);
                        });
                        const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `stats-${(statsDialog.campaign?.name || 'campaign').replace(/[^a-z0-9]/gi, '-').toLowerCase()}.csv`;
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                      data-testid="download-stats-csv"
                    >
                      <Download className="h-4 w-4 mr-1" /> CSV
                    </Button>
                  )}
                </div>
                {statsDialog.data.recipients?.length ? (
                  <div className="max-h-72 overflow-y-auto border rounded-lg text-sm" data-testid="stats-recipients">
                    <table className="w-full">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2">Email</th>
                          <th className="text-center px-2">📬</th>
                          <th className="text-center px-2">👁️</th>
                          <th className="text-center px-2">🖱️</th>
                          <th className="text-center px-2">⚠️</th>
                        </tr>
                      </thead>
                      <tbody>
                        {statsDialog.data.recipients
                          .sort((a, b) => (b.clicked - a.clicked) || (b.opened - a.opened))
                          .map(r => (
                          <tr key={r.email} className="border-t hover:bg-gray-50">
                            <td className="px-3 py-1.5 font-mono text-xs">{r.email}</td>
                            <td className="text-center">{r.delivered ? '✅' : '·'}</td>
                            <td className="text-center">{r.opened ? '✅' : '·'}</td>
                            <td className="text-center">{r.clicked ? '🔥' : '·'}</td>
                            <td className="text-center">{r.bounced ? '❌' : '·'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 py-6 text-center bg-gray-50 rounded">
                    Aún no hay eventos registrados. Los datos aparecerán cuando Resend envíe eventos vía webhook.
                  </p>
                )}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900">
                💡 <strong>Tip:</strong> Para ver aperturas y clicks aquí, configura el webhook en Resend:
                <br />→ <a href="https://resend.com/webhooks" target="_blank" rel="noopener noreferrer" className="underline font-semibold">https://resend.com/webhooks</a>
                <br />→ Endpoint: <code className="bg-white px-1 rounded">https://eyehotelsyevents.es/api/webhooks/resend</code>
                <br />→ Eventos: email.sent, email.delivered, email.opened, email.clicked, email.bounced, email.complained
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Campaigns;
